import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import {pulseUserAtom} from 'atoms/auth';
import {App} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {LabelGroupCell, LabelGroupSubtleCell} from 'components/shared/table/LabelGroupCell';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';

import styles from './ExternalApp.module.css';
import {useExternalAppService} from './hooks';

const DSYSTableRow = ({name, appKey, allowed_ips, permissions}: App & {appKey: string}) => (
  <Table.Row isSelectable className={styles.row}>
    <LabelGroupCell
      isBold
      text={name}
      subText={
        <Text size="small" color="subtle" className={styles.key}>
          {appKey}
        </Text>
      }
    />
    <LabelGroupSubtleCell text={permissions?.join(',')} />
    <LabelGroupSubtleCell text={allowed_ips?.join(',')} />
  </Table.Row>
);

const columns = [
  {type: 'name', minWidth: 470},
  {type: 'permissions', minWidth: 140},
  {type: 'allowed ips', short: 'ips', minWidth: 100},
];

export const ExternalApp = () => {
  const {apps, createApp} = useExternalAppService();
  useDocumentTitle('API keys');

  const handleCreateApp = async () => {
    await createApp({name: 'Dropbox Directory', permissions: ['teams.readonly']});
  };

  const user = useAtomValue(pulseUserAtom);
  if (!user || (user && !isSuperUser(user))) {
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'API keys', to: ROUTE_PATHS.ADMIN_APPS},
      ]}
    >
      <Stack gap="24">
        <Title size={24}>API keys</Title>

        <Button variant="outline" onClick={handleCreateApp} style={{display: 'none'}}>
          Create Dropbox Directory App
        </Button>

        <Table columns={columns} data={apps ?? []}>
          <Table.Header />
          <Table.Body>
            {apps?.map(({key: appKey, ...app}) => (
              <DSYSTableRow key={appKey} appKey={appKey} {...app} />
            ))}
          </Table.Body>
        </Table>
      </Stack>
    </Layout.Container>
  );
};
